import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { trackUserBehavior } from '../utils/analytics';

const PricingSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(180deg, ${props => props.theme.colors.darkGray} 0%, ${props => props.theme.colors.dark} 100%);
  position: relative;
  id: pricing;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/LM Motors_00062.jpg');
    background-size: cover;
    background-position: center;
    opacity: 0.03;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.white};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.lightGray};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PricingCards = styled.div`
  display: flex;
  justify-content: center;
  max-width: 500px;
  margin: 0 auto;
`;

const PricingCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}60, ${props => props.theme.colors.darkGray}80);
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 20px;
  padding: 40px;
  position: relative;
  text-align: center;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s ease;
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px;
    border-radius: 16px;
  }

  transform: scale(1.05);
  box-shadow: 0 20px 60px ${props => props.theme.colors.primary}30;
  animation: popularPulse 4s ease-in-out infinite;

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 25px 70px ${props => props.theme.colors.primary}40;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  }

  @keyframes popularPulse {
    0%, 100% { 
      box-shadow: 0 20px 60px ${props => props.theme.colors.primary}30;
      border-color: ${props => props.theme.colors.primary};
    }
    50% { 
      box-shadow: 0 25px 80px ${props => props.theme.colors.primary}50;
      border-color: ${props => props.theme.colors.secondary};
    }
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: ${props => props.theme.colors.dark};
  padding: 8px 24px;
  border-radius: 0 0 12px 12px;
  font-size: 0.9rem;
  font-weight: 600;
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  margin-bottom: 16px;
  margin-top: 20px;
`;

const OriginalPrice = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.lightGray};
  text-decoration: line-through;
  margin-bottom: 8px;
`;

const Price = styled.div`
  font-family: ${props => props.theme.fonts.display};
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 8px;
`;

const PriceSubtext = styled.div`
  color: ${props => props.theme.colors.lightGray};
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const CouponBadge = styled(motion.div)`
  background: linear-gradient(135deg, #FF6B35, #FFA500);
  color: #000;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 24px;
  text-align: center;
  box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
  border: 2px solid #FFA500;
  
  strong {
    font-size: 1.4rem;
    letter-spacing: 2px;
    display: block;
    margin-top: 4px;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
    padding: 12px 20px;
    
    strong {
      font-size: 1.2rem;
    }
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin-bottom: 40px;
  text-align: left;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  color: ${props => props.theme.colors.lightGray};

  &:before {
    content: '✓';
    color: ${props => props.theme.colors.success};
    font-weight: bold;
    margin-right: 12px;
    font-size: 1.2rem;
  }
`;

const CTAButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: ${props => props.theme.colors.dark};
  border: 2px solid ${props => props.theme.colors.primary};
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px ${props => props.theme.colors.primary}40;
  }
`;

const Guarantee = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.success}10, ${props => props.theme.colors.primary}10);
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  margin-top: 60px;
`;

const GuaranteeTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.success};
  margin-bottom: 16px;
`;

const GuaranteeText = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 1.1rem;
  line-height: 1.6;
`;

const UrgencyTimer = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}20, ${props => props.theme.colors.primary}20);
  border: 1px solid ${props => props.theme.colors.accent}40;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  margin-bottom: 60px;
`;

const TimerTitle = styled.h3`
  color: ${props => props.theme.colors.accent};
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 16px;
`;

const Timer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 16px;
`;

const TimeUnit = styled.div`
  text-align: center;
`;

const TimeNumber = styled(motion.div)`
  font-family: ${props => props.theme.fonts.display};
  font-size: 2rem;
  font-weight: 800;
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.darkGray};
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 60px;
  border: 1px solid ${props => props.theme.colors.accent}40;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
    padding: 8px 12px;
    min-width: 50px;
  }
`;

const TimeLabel = styled.div`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
  margin-top: 8px;
`;

const Pricing = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  // Timer de 24h a partir do primeiro acesso do usuário
  const getEndDate = () => {
    let endDate = localStorage.getItem('offerEndDate');
    if (!endDate) {
      // Primeira visita: define 24h a partir de agora
      endDate = new Date().getTime() + (24 * 60 * 60 * 1000);
      localStorage.setItem('offerEndDate', endDate);
    }
    return parseInt(endDate);
  };
  
  const calculateTimeLeft = () => {
    const now = new Date().getTime();
    const difference = getEndDate() - now;
    
    if (difference <= 0) {
      return { hours: 0, minutes: 0, seconds: 0 };
    }
    
    return {
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000)
    };
  };
  
  const [timeLeft, setTimeLeft] = React.useState(calculateTimeLeft());

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handlePurchase = (plan) => {
    trackUserBehavior.trackConversion('purchase_intent', 497);
    trackUserBehavior.trackEvent('purchase_click', {
      plan,
      timestamp: new Date().toISOString()
    });
    
    // Redirecionando para o checkout da CursEduca
    window.open('https://pay.curseduca.com/leocelini/p/consignacao-com-proposito-1757530675544', '_blank');
  };

  return (
    <PricingSection ref={ref} id="pricing">
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            DOMINE O MÉTODO CCP HOJE MESMO
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Oferta especial de dezembro - Consignação com Propósito completo
          </Subtitle>
        </Header>

        <UrgencyTimer
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <TimerTitle>OFERTA BLACK FRIDAY TERMINA EM:</TimerTitle>
          <Timer>
            <TimeUnit>
              <TimeNumber
                animate={{ scale: [1, 1.02, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {String(timeLeft.hours).padStart(2, '0')}
              </TimeNumber>
              <TimeLabel>Horas</TimeLabel>
            </TimeUnit>
            <TimeUnit>
              <TimeNumber
                animate={{ scale: [1, 1.03, 1] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
              >
                {String(timeLeft.minutes).padStart(2, '0')}
              </TimeNumber>
              <TimeLabel>Minutos</TimeLabel>
            </TimeUnit>
            <TimeUnit>
              <TimeNumber
                animate={{ 
                  scale: [1, 1.05, 1],
                  borderColor: [`${props => props.theme.colors.accent}40`, `${props => props.theme.colors.accent}`, `${props => props.theme.colors.accent}40`]
                }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.6 }}
              >
                {String(timeLeft.seconds).padStart(2, '0')}
              </TimeNumber>
              <TimeLabel>Segundos</TimeLabel>
            </TimeUnit>
          </Timer>
        </UrgencyTimer>

        <PricingCards>
          <PricingCard
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <PopularBadge>OFERTA BLACK FRIDAY</PopularBadge>
            <PlanName>CCP COMPLETO</PlanName>
            <OriginalPrice>De R$ 1.497</OriginalPrice>
            <Price>R$ 497</Price>
            <PriceSubtext>Pagamento único</PriceSubtext>
            <CouponBadge
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🎟️ USE O CUPOM:
              <strong>CCPBLACK</strong>
            </CouponBadge>
            
            <FeaturesList>
              <Feature>Módulos completos em vídeo HD</Feature>
              <Feature>Suporte prioritário por 6 meses</Feature>
              <Feature>Certificado de conclusão</Feature>
              <Feature>Acesso vitalício</Feature>
            </FeaturesList>
            
            <CTAButton
              onClick={() => handlePurchase('blackfriday')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              GARANTIR OFERTA BLACK FRIDAY
            </CTAButton>
          </PricingCard>
        </PricingCards>

        <Guarantee
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <GuaranteeTitle>GARANTIA INCONDICIONAL DE 7 DIAS</GuaranteeTitle>
          <GuaranteeText>
            Se você não ficar 100% satisfeito com o curso ou não ver resultados práticos em seu negócio, 
            devolvemos todo seu dinheiro sem perguntas. Sua satisfação é nossa prioridade.
          </GuaranteeText>
        </Guarantee>
      </Container>
    </PricingSection>
  );
};

export default Pricing;